import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  computeAudienceBehaviour,
  fetchCreatorDrops,
  fetchFirstViewsByCreator,
  fetchFollowsForCreator,
} from "../../services/creatorStudioService";

function LoyaltyRow({ label, value, valueClass = "", muted = false }) {
  return (
    <div className="cs-loyalty-row">
      <div className="cs-loyalty-label">{label}</div>
      <div className={`cs-loyalty-value${valueClass ? ` ${valueClass}` : ""}${muted ? " muted" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function formatUnlockDays(days) {
  if (days == null || Number.isNaN(days)) return "—";
  if (days < 1) return "Same day";
  if (days < 2) return "1 day after following";
  return `${days.toFixed(1)} days after following`;
}

export default function AudienceInsights() {
  const { currentUser } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [follows, firstViews, drops] = await Promise.all([
          fetchFollowsForCreator(currentUser.uid),
          fetchFirstViewsByCreator(currentUser.uid),
          fetchCreatorDrops(currentUser.uid),
        ]);
        if (!cancelled) {
          setMetrics(computeAudienceBehaviour({ follows, firstViews, drops }));
        }
      } catch (err) {
        console.error("AudienceInsights load error:", err);
        if (!cancelled) setMetrics(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  if (loading) {
    return (
      <div className="cs-card">
        <div className="cs-card-title">Audience behaviour</div>
        <div className="cs-card-sub">How fans engage with your drops</div>
        <div className="cs-empty" style={{ padding: 20 }}>Loading…</div>
      </div>
    );
  }

  if (!metrics?.hasData) {
    return (
      <div className="cs-card">
        <div className="cs-card-title">Audience behaviour</div>
        <div className="cs-card-sub">How fans engage with your drops</div>
        <div className="cs-empty" style={{ padding: 20 }}>
          Post drops and grow your following to see audience behaviour insights.
        </div>
      </div>
    );
  }

  return (
    <div className="cs-card">
      <div className="cs-card-title">Audience behaviour</div>
      <div className="cs-card-sub">How fans engage with your drops</div>

      <LoyaltyRow
        label="New vs returning viewers"
        value={
          <>
            {metrics.newPct}% new · <span className="green">{metrics.returningPct}% returning</span>
          </>
        }
      />

      <LoyaltyRow
        label="Avg time before first unlock"
        value={formatUnlockDays(metrics.avgUnlockDays)}
        valueClass="gold"
      />

      <LoyaltyRow
        label="Fan churn rate this month"
        value={metrics.churnRate != null ? `${metrics.churnRate}% unfollowed` : "—"}
        valueClass={metrics.churnRate != null ? "red" : ""}
      />

      <LoyaltyRow
        label="Drops that caused unfollows"
        value={metrics.churnDropLabel || "No pattern detected yet"}
        muted
      />

      <LoyaltyRow
        label="Fans following 30+ days"
        value={metrics.longTermActivePct != null ? `${metrics.longTermActivePct}% still active` : "—"}
        valueClass="green"
      />

      <LoyaltyRow
        label="First 100 repeat viewers"
        value={metrics.repeatFirst100Pct != null ? `${metrics.repeatFirst100Pct}% return drop to drop` : "—"}
        valueClass="gold"
      />
    </div>
  );
}
